import React, { FC, useContext, useState } from "react";
import { Form, Modal, Button, Alert, Row, Col } from "react-bootstrap";
import { useFormik } from "formik";
import * as yup from "yup";
import ClipLoader from "react-spinners/ClipLoader";
import { CoupleType, PersonType } from "../../../../types/PersonType";
import { PersonModel } from "../../../../models/PersonModel";
import { isResponseError, OptionType, ResponseErrorType } from "../../../../types/CommonType";
import TranslateTerms from "../../../../components/TranslateTerms";
import { FormattedMessage, useIntl } from "react-intl";
import { LANG_COMMON_CANCEL, LANG_COMMON_DIAGNOSIS, LANG_COMMON_EVOLUTION, LANG_COMMON_LEGAL_ID, LANG_COMMON_LOADING, LANG_COMMON_MEDICAL_RECORD_NUMBER, LANG_COMMON_NAME, LANG_COMMON_NEW, LANG_COMMON_PATIENT, LANG_COMMON_SAVE, LANG_ERROR, LANG_WARNING_FILL_ALL } from "../../../../lang";
import InputMask from 'react-input-mask';
import clsx from "clsx";
import { CoupleModel } from "../../../../models/CoupleModel";
import { MedicalRecordEvolutionType, MedicalRecordType } from "../../../../types/MedicalRecordType";
import { MedicalRecordModel } from "../../../../models/MedicalRecordModel";
import { DefaultUser } from "../../../../providers/DefaultData";
import { BtnBold, BtnBulletList, BtnItalic, BtnNumberedList, BtnStrikeThrough, BtnUnderline, BtnUndo, Editor, EditorProvider, Separator, Toolbar } from "react-simple-wysiwyg";
import { TreatmentMedicationType, TreatmentType } from "../../../../types/TreatmentType";
import { TreatmentModel } from "../../../../models/TreatmentModel";
import CustomSelect from "../../../../components/form/CustomSelect";
import moment, { duration } from "moment";
import Select from "react-select/dist/declarations/src/Select";
import { EventType } from "../../../../types/EventType";
import { EventModel } from "../../../../models/EventModel";
import EventTypeSelect from "../../../../components/form/EventTypeSelect";
import { AuthContext } from "../../../../providers/AuthContextProvider";
import CalendarSelect from "../../../../components/form/CalendarSelect";
import CoupleSelect from "../../../../components/form/CoupleSelect";
import Confirm from "../../../../components/messages/Confirm";
import CoupleAddModal from "../../../couple/components/modal/CoupleAddModal";
import PersonEditModal from "../../../couple/components/modal/PersonEditModal";

interface Props {
    show: boolean
    onHide: () => void
    onSave: (event: EventType) => void
    onDelete?: (event_id: number) => void
    event: EventType | null
}

const EventModal: FC<Props> = ({ show, onHide, onSave, onDelete, event }) => {
    const schema = yup.object().shape({
        date: yup.string().required(),
        time: yup.string().required(),
        duration: yup.number().required(),
        notes: yup.string().nullable(),
        event_type_id: yup.number().nullable(),
        calendar_id: yup.number().required(),
        couple_id: yup.number().required()
    });

    const [loading, setLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    const intl = useIntl();
    const DefaultErrorMsg = intl.formatMessage(LANG_ERROR);
    const [errorMsg, setErrorMsg] = useState(DefaultErrorMsg);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditPrimaryModal, setShowEditPrimaryModal] = useState(false);

    const initialEvent: EventType = event ? { ...event } : {
        id: 0,
        couple_id: 0,
        calendar_id: 0,
        date: moment().format('YYYY-MM-DD'),
        time: moment().format('HH:mm:ss'),
        status: 'scheduled',
        notes: '',
        duration: 15
    };

    const { user } = useContext(AuthContext);

    const onSubmit = async (values: EventType) => {
        // Validate Calendar
        if (!values.calendar_id || values.calendar_id === 0) {
            setHasError(true);
            setErrorMsg("Selecione uma agenda!");
            return;
        }

        // Validate Couple
        if (!values.couple_id || values.couple_id === 0) {
            setHasError(true);
            setErrorMsg("Selecione um paciente!");
            return;
        }

        // Create/Update
        setHasError(false);
        setErrorMsg(DefaultErrorMsg);
        setLoading(true);

        let resp: EventType | ResponseErrorType = (event === null || event.id === 0) ?
            await EventModel().create(values)
            : await EventModel().update(event.id, values);
        setLoading(false);
        // Saving result
        if (!isResponseError(resp)) {
            onSave(resp as EventType);
        } else {
            setHasError(true);
            setErrorMsg((resp as ResponseErrorType).message);
        }
    }

    const formik = useFormik({
        validationSchema: schema,
        onSubmit: onSubmit,
        initialValues: initialEvent,
    });

    return (
        <>
            {showDeleteConfirm && event && (
                <Confirm
                    show={showDeleteConfirm}
                    message={`Tem certeza que deseja excluir o agendamento do paciente ${CoupleModel().formatCoupleName(event.couple)} no dia ${moment(event.date).format('DD/MM/YYYY')} às ${event.time}h?`}
                    onConfirm={async () => {
                        setShowDeleteConfirm(false);
                        setLoading(true);
                        let resp: boolean = await EventModel().remove(event.id);
                        setLoading(false);
                        // Deletion result
                        if (resp) {
                            if (onDelete) onDelete(event.id);
                        } else {
                            setHasError(true);
                            setErrorMsg("Não foi possível excluir o agendamento.");
                        }
                    }}
                    onHide={() => { setShowDeleteConfirm(false); }}
                />
            )}
            <CoupleAddModal
                show={showAddModal}
                onHide={() => setShowAddModal(false)}
                onSave={(couple) => {
                    setShowAddModal(false);
                    if (couple && event) {
                        event.couple = couple;
                        setShowEditPrimaryModal(true);
                    }
                }} />
            {event && event.couple && event.couple.primary && (
                <PersonEditModal
                    show={showEditPrimaryModal}
                    person={event.couple?.primary}
                    onHide={() => setShowEditPrimaryModal(false)}
                    onSave={(person) => {
                        setShowEditPrimaryModal(false);
                        event.couple!.primary = person;
                        formik.setFieldValue('couple_id', person.couple_id); // Set the value of CoupleSelect
                    }}
                />
            )}
            <Modal
                show={show && !showDeleteConfirm && !showAddModal && !showEditPrimaryModal}
                onHide={() => {
                    onHide();
                }}
                className="modal-default"
                size="lg"
                backdrop="static"

            >
                <Form noValidate onSubmit={(e) => {
                    setHasSubmitted(true);
                    formik.setTouched({
                        date: true,
                        time: true,
                        notes: true,
                        event_type_id: true,
                        calendar_id: true,
                        couple_id: true
                    });
                    formik.handleSubmit(e);
                }}>
                    <Modal.Header closeButton closeVariant="white">
                        <Modal.Title>
                            Agendamento
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {(!formik.isValid && hasSubmitted) && (<Alert variant={'danger'}><FormattedMessage {...LANG_WARNING_FILL_ALL} /></Alert>)}
                        {hasError && (<Alert variant={'danger'}>{errorMsg}</Alert>)}
                        <Row>
                            <Col lg={4}>
                                <Form.Group className="mb-3" controlId="name">
                                    <Form.Label>Tipo</Form.Label>
                                    <EventTypeSelect
                                        name="event_type_id"
                                        value={formik.values.event_type_id}
                                        onChange={(e: OptionType | null) => {
                                            formik.setFieldValue('event_type_id', e ? e.value : null);

                                            // Find selected event type to set default duration
                                            const selectedEventType = user?.business?.event_types?.find(et => et.id === (e ? e.value : null));
                                            console.log(selectedEventType);
                                            if (selectedEventType && selectedEventType.duration) {
                                                formik.setFieldValue('duration', selectedEventType.duration);
                                            }
                                        }}
                                        isValid={formik.touched.event_type_id && !formik.errors.event_type_id}
                                        isTouched={formik.touched.event_type_id}
                                        disabled={loading}
                                        placeholder="Selecionar..."
                                        eventTypes={user?.business?.event_types || []}
                                    />
                                </Form.Group>
                            </Col>
                            <Col lg={4}>
                                <Form.Group className="mb-3" controlId="name">
                                    <Form.Label>Data</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="date"
                                        value={formik.values.date}
                                        onChange={formik.handleChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col lg={2}>
                                <Form.Group className="mb-3" controlId="name">
                                    <Form.Label>Horário</Form.Label>
                                    <Form.Select
                                        className="form-control"
                                        value={formik.values.time}
                                        name="time"
                                        onChange={formik.handleChange}
                                    >
                                        {EventModel().generateTimeOptions().map((option) => (
                                            <option key={option.key} value={option.value}>{option.label}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col lg={2}>
                                <Form.Group className="mb-3" controlId="name">
                                    <Form.Label>Duração</Form.Label>
                                    <Form.Select
                                        className="form-control"
                                        value={formik.values.duration}
                                        name="duration"
                                        onChange={formik.handleChange}
                                    >
                                        {EventModel().generateDurationOptions().map((option) => (
                                            <option key={option.key} value={option.value}>{option.label}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col lg={6}>
                                <Form.Group className="mb-3" controlId="name">
                                    <Form.Label>Agenda</Form.Label>
                                    <CalendarSelect
                                        name="calendar_id"
                                        value={formik.values.calendar_id || undefined}
                                        onChange={(e: OptionType | null) => {
                                            formik.setFieldValue('calendar_id', e ? e.value : null);
                                        }}
                                        isValid={formik.touched.calendar_id && !formik.errors.calendar_id}
                                        isTouched={formik.touched.calendar_id}
                                        disabled={loading}
                                        placeholder="Selecionar..."
                                        calendars={user?.calendars}
                                    />
                                </Form.Group>
                            </Col>
                            <Col lg={4}>
                                <Form.Group className="mb-3" controlId="name">
                                    <Form.Label>Paciente</Form.Label>
                                    <CoupleSelect
                                        key={formik.values.couple_id || 'new'}
                                        name="couple_id"
                                        value={formik.values.couple_id}
                                        onChange={(e: OptionType | null, couple: CoupleType) => {
                                            formik.setFieldValue('couple_id', e ? e.value : null);
                                            event!.couple = couple;
                                        }}
                                        isValid={formik.touched.couple_id && !formik.errors.couple_id}
                                        isTouched={formik.touched.couple_id}
                                        disabled={loading}
                                        placeholder="Selecionar..."
                                        isSearchable={true}
                                        couples={event?.couple ? [event.couple] : []}
                                    />
                                </Form.Group>
                            </Col>
                            <Col lg={2}>
                                <Button disabled={loading} variant="outline-light" onClick={() => {
                                    setShowAddModal(true);
                                }} className="btn-custom bg-custom-gradient btn btn-primary mt-4">
                                    <i className="bi bi-plus-lg"></i>
                                </Button>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Form.Group className="mb-3" controlId="name">
                                    <Form.Label>Observações</Form.Label>
                                    <EditorProvider>
                                        <Editor
                                            name="content"
                                            placeholder="Outras informações"
                                            value={formik.values.notes}
                                            onChange={(e) => {
                                                formik.setFieldValue('notes', e.target.value);
                                            }}
                                        >
                                            <Toolbar>
                                                <BtnBold />
                                                <BtnItalic />
                                                <BtnUnderline />
                                                <BtnStrikeThrough />
                                                <Separator />
                                                <BtnNumberedList />
                                                <BtnBulletList />
                                            </Toolbar>
                                        </Editor>
                                    </EditorProvider>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        {event && event.id !== 0 && (
                            <Button disabled={loading} variant="outline-light" onClick={() => {
                                setShowDeleteConfirm(true);
                            }} className="btn-custom btn-custom-light">
                                <span className="text-danger">
                                    <i className="bi bi-trash"></i> Excluir
                                </span>
                            </Button>
                        )}
                        <Button disabled={loading} variant="outline-light" onClick={onHide} className="btn-custom btn-custom-light">
                            <FormattedMessage {...LANG_COMMON_CANCEL} />
                        </Button>
                        <Button
                            variant="primary"
                            type={"submit"} disabled={loading} className="btn-custom bg-custom-gradient">
                            {loading ? <><ClipLoader color={'#ffffff'} loading={loading} size={'12px'} /> <FormattedMessage {...LANG_COMMON_LOADING} /></> : <FormattedMessage {...LANG_COMMON_SAVE} />}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </>
    )
}

export default EventModal;